import React, { useEffect, useRef, useState } from "react";

/**
 * Modal for saving an agent blueprint into the Tudástár.
 * - First-time save (no existing entry): single "Név" field + Mentés.
 * - Subsequent save (existing entry present): "Felülírás" vs "Új mentés"
 *   choice, with a Név field shown only when "Új mentés" is selected.
 */
export default function SaveToVaultModal({
  open,
  existing,
  defaultName,
  onCancel,
  onConfirm,
  saving,
}) {
  const [mode, setMode] = useState(existing ? "overwrite" : "new");
  const [name, setName] = useState(defaultName || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMode(existing ? "overwrite" : "new");
      setName(defaultName || "");
    }
  }, [open, existing, defaultName]);

  useEffect(() => {
    if (open && (!existing || mode === "new")) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open, mode, existing]);

  if (!open) return null;

  const isFirstTime = !existing;
  const showNameField = isFirstTime || mode === "new";
  const trimmedName = (name || "").trim();
  const canSubmit =
    !saving &&
    (mode === "overwrite" ||
      (showNameField && trimmedName.length > 0 && trimmedName.length <= 80));

  const submit = () => {
    if (!canSubmit) return;
    onConfirm({
      mode: existing ? mode : "new",
      name: showNameField ? trimmedName : existing?.name || trimmedName,
      existingId: existing?.id,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={() => !saving && onCancel()}
    >
      <div
        data-testid="save-to-vault-modal"
        className="rounded-xl"
        style={{
          background: "#181822",
          border: "1px solid rgba(255,255,255,0.08)",
          padding: "26px 28px",
          maxWidth: 460,
          width: "92%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[16px] text-white font-semibold mb-1">
          Mentés a Tudástárba
        </div>
        <div className="text-[12px] text-[#a1a1a1] mb-5">
          {isFirstTime
            ? "Hozz létre egy első verziót a Tudástárban."
            : "Felülírod a meglévőt, vagy új verzióként mented?"}
        </div>

        {/* Two-option chooser when an existing entry already exists */}
        {!isFirstTime && (
          <div className="flex flex-col gap-2 mb-5" data-testid="save-mode-options">
            <ModeOption
              testid="save-mode-overwrite"
              active={mode === "overwrite"}
              onClick={() => !saving && setMode("overwrite")}
              title="Felülírás"
              description={`A meglévő »${existing.name}« lecserélődik az új verzióra.`}
            />
            <ModeOption
              testid="save-mode-new"
              active={mode === "new"}
              onClick={() => !saving && setMode("new")}
              title="Új mentés"
              description="Mindkettő megmarad a Tudástárban."
            />
          </div>
        )}

        {/* Name field (always for first-time, only for "Új mentés" otherwise) */}
        {showNameField && (
          <div className="mb-5">
            <label className="block text-[11px] text-[#a1a1a1] uppercase tracking-[0.18em] mb-2">
              Név
            </label>
            <input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape" && !saving) onCancel();
              }}
              maxLength={80}
              data-testid="save-modal-name-input"
              placeholder="pl. Márkahang - 2026.04.29."
              className="w-full text-white text-[14px] focus:outline-none"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.10)",
                borderRadius: 10,
                padding: "11px 14px",
              }}
            />
            {trimmedName.length > 80 && (
              <div className="text-[11px] mt-1" style={{ color: "#e57373" }}>
                Maximum 80 karakter
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            data-testid="save-modal-cancel"
            className="px-4 py-2 rounded-lg text-[13px]"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.10)",
              color: "#c0c0d0",
            }}
          >
            Mégsem
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="save-modal-confirm"
            className="px-4 py-2 rounded-lg text-[13px] font-medium"
            style={{
              background: canSubmit ? "rgba(124,108,242,0.15)" : "rgba(124,108,242,0.07)",
              border: `1px solid ${canSubmit ? "rgba(124,108,242,0.40)" : "rgba(124,108,242,0.20)"}`,
              color: canSubmit ? "#c5bbff" : "#7a72b0",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {saving ? "Mentés…" : "Mentés"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeOption({ active, onClick, title, description, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-left rounded-lg w-full"
      style={{
        background: active ? "rgba(124,108,242,0.10)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${active ? "rgba(124,108,242,0.40)" : "rgba(255,255,255,0.08)"}`,
        padding: "12px 14px",
        cursor: "pointer",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="rounded-full mt-1 flex items-center justify-center"
          style={{
            width: 16,
            height: 16,
            border: `1px solid ${active ? "#a89bff" : "rgba(255,255,255,0.30)"}`,
            background: active ? "#7c6cf2" : "transparent",
            flexShrink: 0,
          }}
        >
          {active && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "white",
                display: "block",
              }}
            />
          )}
        </div>
        <div className="flex-1">
          <div className="text-[13.5px] text-white font-medium mb-1">{title}</div>
          <div className="text-[11.5px] text-[#a1a1a1] leading-relaxed">{description}</div>
        </div>
      </div>
    </button>
  );
}
